import { TimeUnits } from '@shared/types'
import { convertToMs } from '@shared/utils'
import { hashKey, keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type ColumnFiltersState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  useReactTable,
} from '@tanstack/react-table'
import { useDebounce } from 'hooks/use-debounce'
import * as React from 'react'
import { z } from 'zod'
import { DEFAULT_PAGE_SIZE } from '../config'
import type { ExtractedTableData, RemoteTableProps, SimpleTableProps } from '../types'

const schema = z.object({
  page: z.coerce.number().default(1),
  limit: z.coerce.number().optional(),
  offset: z.coerce.number().optional(),
  sort: z.string().optional(),
})

// Cursor for page `to` from page `from`: only a step to a neighbour can reuse the current links.
const cursorFor = (
  from: number,
  to: number,
  links?: Pick<ExtractedTableData<unknown>, 'nextCursor' | 'previousCursor'>,
) => {
  if (to === 0) return undefined // page 1 is offset 0 in every server mode
  if (to === from + 1) return links?.nextCursor
  if (to === from - 1) return links?.previousCursor
  return undefined // a jump falls back to offset, which cursor-only servers answer with page 1
}

export function useDataTable<TData, TApi>({
  columns,
  filterFields,
  extractData,
  fetchService,
  clientTable = false,
  queryKey = [],
  refetchInterval,
  enableOffsetMode = false,
  ignoreUrlPagination = false,
  enablePrefetchWithFilters = false,
  staleTime,
  columnVisibility,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  showLoader = false,
  onSelectionChange,
  idKey,
  resetSelectionOnPageChange,
  accumulateSelection,
  onUrlChange,
}: RemoteTableProps<TData, TApi>) {
  const searchParams = React.useMemo(() => new URLSearchParams(window.location.search), [])
  const queryClient = useQueryClient()

  const search = schema.parse(Object.fromEntries(searchParams))
  // Ignore pagination params from URL if ignoreUrlPagination is true
  const { page, offset } = ignoreUrlPagination ? { page: undefined, offset: undefined } : search
  // When enableOffsetMode is true, always use defaultPageSize to ensure consistent pagination
  // Otherwise, use limit from URL or defaultPageSize
  const limit = enableOffsetMode
    ? defaultPageSize
    : ignoreUrlPagination
      ? defaultPageSize
      : (search.limit ?? defaultPageSize)

  // Calculate initial pageIndex based on mode
  const initialPageIndex = React.useMemo(() => {
    if (ignoreUrlPagination) {
      // Always start from page 0 when ignoring URL pagination
      return 0
    }
    if (enableOffsetMode && offset !== undefined) {
      // For offset mode, calculate pageIndex from offset using defaultPageSize
      // This ensures we use the correct pageSize for offset calculations
      return Math.floor(offset / defaultPageSize)
    } else if (enableOffsetMode) {
      // If no offset in URL, start from page 0
      return 0
    } else {
      // For traditional mode, use page
      return (page ?? 1) - 1
    }
  }, [enableOffsetMode, ignoreUrlPagination, offset, defaultPageSize, page])

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: limit,
  })
  // Cursor that fetched the current page (undefined = offset mode); never written to the URL.
  const [cursor, setCursor] = React.useState<string>()

  // Memoize computation of searchableColumns and filterableColumns
  const { searchableColumns, filterableColumns, filterMaps } = React.useMemo(
    () => ({
      searchableColumns: filterFields?.filter((field) => !field.options),
      filterableColumns: filterFields?.filter((field) => field.options),
      filterMaps: filterFields?.reduce<{
        normal: Record<string, string>
        reverse: Record<string, string>
      }>(
        (acc, field) => {
          if (field.value && field.paramKey) {
            acc.normal[field.value as string] = field.paramKey
            acc.reverse[field.paramKey] = field.value as string
          }
          return acc
        },
        {
          normal: {},
          reverse: {},
        },
      ),
    }),
    [filterFields],
  )

  const initialColumnFilters: ColumnFiltersState = React.useMemo(
    () =>
      Array.from(searchParams.entries()).reduce<ColumnFiltersState>((filters, [key, value]) => {
        if (['page', 'limit', 'offset', 'cursor'].includes(key)) return filters // Skip pagination params
        const filterableColumn = filterableColumns?.find(
          (column) => column.value === key || column.paramKey === key,
        )
        const dateRangeColumn = filterFields?.find(
          (field) =>
            (field.isDateRange || field.isMonthYearRange) &&
            (field.value === key || field.paramKey === key),
        )

        filters.push({
          id: filterMaps?.reverse[key] ?? key,
          value: dateRangeColumn ? value : filterableColumn ? value.split(',') : value,
        })
        return filters
      }, []),
    [filterableColumns, searchableColumns, searchParams, filterFields],
  )

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(initialColumnFilters)
  const [rowSelection, setRowSelection] = React.useState({})
  // All selection mutations must flow through handleRowSelection to keep cache in sync.
  const selectionCacheRef = React.useRef<Map<string, TData>>(new Map())
  const debouncedColumnFilters = useDebounce(columnFilters, 300)
  const isInitialMount = React.useRef(true)

  // Mark initial mount as complete after first render
  React.useEffect(() => {
    isInitialMount.current = false
  }, [])

  const newSearchParams = new URLSearchParams()

  columnFilters.forEach((filter) => {
    const paramKey = filterMaps?.normal[filter.id] ?? filter.id
    const filterField = filterFields?.find(
      (field) => field.value === filter.id || field.paramKey === filter.id,
    )

    if (filterField?.isDateRange || filterField?.isMonthYearRange) {
      // For date range filters, the value is already a serialized string
      newSearchParams.append(paramKey, filter.value as string)
    } else if (Array.isArray(filter.value)) {
      // For multi-select filters, join with comma
      newSearchParams.append(paramKey, filter.value.join(','))
    } else {
      // For regular filters
      newSearchParams.append(paramKey, filter.value as string)
    }
  })

  const tableQueryKey = (
    p: typeof pagination,
    d: typeof debouncedColumnFilters = [],
    c?: string,
  ) => (clientTable ? queryKey : c ? [...queryKey, p, d, c] : [...queryKey, p, d])

  // Query params for page `p` on top of the current filters, by cursor when we hold one.
  const pageParams = (p: PaginationState, c?: string) => {
    const params = new URLSearchParams(newSearchParams)
    params.set('limit', (enableOffsetMode ? defaultPageSize : p.pageSize).toString())
    if (c) params.set('cursor', c)
    else if (enableOffsetMode) params.set('offset', (p.pageIndex * defaultPageSize).toString())
    else params.set('page', (p.pageIndex + 1).toString())
    return params
  }

  // URL always says offset, never cursor: it is persisted, and a replayed cursor is a 400.
  React.useEffect(() => {
    if (ignoreUrlPagination) return
    window.history.replaceState({}, '', `${window.location.pathname}?${pageParams(pagination)}`)
    onUrlChange?.(window.location.search)
  }, [
    pagination,
    debouncedColumnFilters,
    enableOffsetMode,
    ignoreUrlPagination,
    defaultPageSize,
    onUrlChange,
  ])

  // Retry presentation must follow the same identity as the active query. A
  // previous filter's request can remain in flight after its observer moves on.
  const currentQueryKey = tableQueryKey(pagination, debouncedColumnFilters, cursor)
  const queryIdentity = hashKey(currentQueryKey)

  // Query part
  const {
    data: tableData,
    isFetching,
    isLoading,
    isPlaceholderData,
    error,
    refetch,
  } = useQuery<TApi, unknown, ExtractedTableData<TData>>({
    queryKey: currentQueryKey,
    staleTime: staleTime ?? convertToMs(1, TimeUnits.Minutes),
    queryFn: async () => {
      const response = await fetchService(
        clientTable ? {} : Object.fromEntries(pageParams(pagination, cursor)),
      )
      return response.data
    },
    refetchInterval,
    select: extractData,
    placeholderData: keepPreviousData,
  })

  // The API rejects a stale or mismatched cursor with a 400: restart the walk from page 1 by offset.
  const cursorRejected =
    !!cursor && (error as { response?: { status?: number } } | null)?.response?.status === 400
  React.useEffect(() => {
    if (!cursorRejected) return
    setCursor(undefined)
    setPagination((p) => ({ ...p, pageIndex: 0 }))
  }, [cursorRejected])

  // Once a page lands: a stale offset relabels as page 1, otherwise prefetch the next page.
  const loaded = tableData !== undefined && !isPlaceholderData
  const nextCursor = loaded ? tableData.nextCursor : undefined
  React.useEffect(() => {
    if (!loaded || clientTable) return
    if (!cursor && tableData?.pageOffset === 0 && pagination.pageIndex > 0) {
      setPagination((p) => ({ ...p, pageIndex: 0 })) // server ignored our offset: this is page 1
      return
    }
    if (debouncedColumnFilters.length && !enablePrefetchWithFilters) return
    if (tableData?.hasNext === false) return // strict: totalCount-only tables keep prefetching
    const nextPage = { ...pagination, pageIndex: pagination.pageIndex + 1 }
    queryClient.prefetchQuery({
      queryKey: tableQueryKey(nextPage, debouncedColumnFilters, nextCursor),
      queryFn: async () => {
        const response = await fetchService(Object.fromEntries(pageParams(nextPage, nextCursor)))
        return response.data
      },
    })
  }, [loaded, nextCursor, cursor, pagination, debouncedColumnFilters, enablePrefetchWithFilters])

  // Memoize the data for the table
  const mData = React.useMemo(() => tableData?.data ?? [], [tableData?.data])

  // Determine pagination mode
  const isOffsetMode =
    enableOffsetMode ||
    (tableData?.totalCount === undefined &&
      (tableData?.hasNext !== undefined || tableData?.hasPrevious !== undefined))

  // Calculate rowCount for pagination
  const rowCount = React.useMemo(() => {
    if (clientTable) return mData.length
    if (tableData?.totalCount !== undefined) return tableData.totalCount

    // For offset mode without totalCount, return undefined to show "many"
    return undefined
  }, [clientTable, tableData, mData.length])

  const resolveRowId = (row: TData, index: number) =>
    idKey ? String((row as Record<string, unknown>)[idKey]) : String(index)

  const resetSelection = () => {
    selectionCacheRef.current.clear()
    setRowSelection({})
    onSelectionChange?.([])
  }

  const handleRowSelection = (updaterOrValue: React.SetStateAction<Record<string, boolean>>) => {
    const next =
      typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection) : updaterOrValue
    setRowSelection(next)
    if (onSelectionChange) {
      if (accumulateSelection && idKey) {
        const cache = selectionCacheRef.current
        for (const row of mData) {
          const rowId = resolveRowId(row, 0)
          if (next[rowId]) cache.set(rowId, row)
          else cache.delete(rowId)
        }
        onSelectionChange(Array.from(cache.values()))
      } else {
        const selectedRows = mData.filter((row, index) => next[resolveRowId(row, index)])
        onSelectionChange(selectedRows)
      }
    }
  }

  const table = useReactTable({
    data: mData,
    rowCount,
    columns,
    enableRowSelection: true,
    getRowId: idKey ? (row: TData) => resolveRowId(row, 0) : undefined,
    state: {
      pagination,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    onColumnFiltersChange: (filters) => {
      setColumnFilters(filters)
      // Only reset pagination if not initial mount (to preserve page from URL)
      if (!isInitialMount.current) {
        setPagination({ pageIndex: 0, pageSize: pagination.pageSize })
        setCursor(undefined) // a cursor is tied to the filters that produced it
        if (accumulateSelection) resetSelection()
      }
    },
    onPaginationChange: (updater) => {
      const to = (typeof updater === 'function' ? updater(pagination) : updater).pageIndex
      const next = cursorFor(pagination.pageIndex, to, tableData)
      if (next && isPlaceholderData) return // a placeholder page's links belong to the page before
      setCursor(next)
      setPagination(updater)
      if (resetSelectionOnPageChange) resetSelection()
    },
    onRowSelectionChange: handleRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    manualPagination: !clientTable,
    manualSorting: !clientTable,
    manualFiltering: !clientTable,
    // Override pagination functions for offset mode
    ...(isOffsetMode && {
      getCanPreviousPage: () => tableData?.hasPrevious ?? false,
      getCanNextPage: () => tableData?.hasNext ?? false,
    }),
  })

  // Show loader during initial load or when explicitly enabled
  // This maintains backward compatibility with existing tables
  const shouldShowLoader = isLoading || (showLoader && isFetching)

  return {
    table,
    isPending: shouldShowLoader,
    error,
    refetch,
    queryIdentity,
    offsetOptions: {
      isOffsetMode: enableOffsetMode || isOffsetMode,
      hasNext: tableData?.hasNext ?? false,
      hasPrevious: tableData?.hasPrevious ?? false,
    },
  }
}

export const useSimpleTable = <TData>(props: SimpleTableProps<TData>) => {
  const { columns, data } = props
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // Do not enable pagination row model for simple table. It will default to 10 rows per page & break for client side tables.
    // getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  })
  return { table }
}
